import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FileText, Check } from 'lucide-react';
import '../nativeinventory/NativeInventoryAdjustmentNew.css';

// Invoice / Credit Memo (Native) — bill a customer without NetSuite in the
// loop. Pick a customer (acct_customers search), optionally prefill lines
// from a mirrored sales order (type its SO number), edit quantities/rates,
// and post. Posting writes the native document + the verified CustInvc /
// CustCred GL shape (DR A/R 1110 / CR income accounts + shipping for an
// invoice; the exact flip for a credit memo) and best-effort creates the
// NetSuite record (tobeemailed:false, echo-deduped).
// Reuses the adjustment screen's stylesheet (same house template).

const money = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};
const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
const newRef = (kind) => `${kind === 'creditMemo' ? 'CM' : 'INV'}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

export default function NativeInvoiceNew() {
  const [docType, setDocType] = useState('invoice'); // 'invoice' | 'creditMemo'
  const [writesEnabled, setWritesEnabled] = useState(null);

  // customer
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customer, setCustomer] = useState(null);

  // from-SO prefill
  const [soRef, setSoRef] = useState('');
  const [soLoading, setSoLoading] = useState(false);
  const [soInfo, setSoInfo] = useState(null);

  // lines
  const [lines, setLines] = useState([]);
  const [itemQuery, setItemQuery] = useState('');
  const [itemResults, setItemResults] = useState([]);

  const [shippingTotal, setShippingTotal] = useState('');
  const [memo, setMemo] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const sourceKey = useRef(newRef('invoice'));

  useEffect(() => {
    axios.get('/api/native-invoices')
      .then(({ data }) => setWritesEnabled(data.writesEnabled === true))
      .catch(() => setWritesEnabled(null));
  }, []);

  useEffect(() => { sourceKey.current = newRef(docType); }, [docType]);

  // Customer search (debounced) — same endpoint the manual-order screen uses.
  useEffect(() => {
    if (customerQuery.trim().length < 2 || customer) { setCustomerResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-orders/customers', { params: { q: customerQuery.trim() } });
        setCustomerResults(data.customers || []);
      } catch {
        setCustomerResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [customerQuery, customer]);

  // Item search (debounced) for adding lines.
  useEffect(() => {
    if (itemQuery.trim().length < 2) { setItemResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get('/api/native-items', { params: { q: itemQuery.trim(), pageSize: 10 } });
        setItemResults(data.items || []);
      } catch {
        setItemResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [itemQuery]);

  const loadFromSo = async () => {
    const ref = soRef.trim();
    if (!ref) return;
    setSoLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`/api/native-invoices/from-so/${encodeURIComponent(ref)}`);
      setSoInfo(data.so);
      setLines((data.lines || []).map((l, i) => ({
        key: `so-${i}`,
        itemIdOrSku: l.itemId || l.sku,
        sku: l.sku,
        name: l.name,
        quantity: String(l.quantity),
        rate: String(l.rate ?? ''),
      })));
      if (!customer && data.so?.customerNsId) {
        setCustomer({ nsCustomerId: data.so.customerNsId, name: data.so.customerName, source: 'sales order' });
      }
    } catch (err) {
      setSoInfo(null);
      setError(err.response?.data?.error || err.message);
    } finally {
      setSoLoading(false);
    }
  };

  const addItem = (r) => {
    setLines((prev) => [...prev, {
      key: `it-${Date.now()}-${prev.length}`,
      itemIdOrSku: r.id,
      sku: r.sku,
      name: r.name,
      quantity: '1',
      rate: r.basePrice != null ? String(r.basePrice) : '',
    }]);
    setItemQuery('');
    setItemResults([]);
  };

  const setLine = (key, field, value) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  };
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const subtotal = round2(lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0));
  const shipping = round2(Number(shippingTotal) || 0);
  const total = round2(subtotal + shipping);
  const linesValid = lines.length > 0 && lines.every((l) => Number(l.quantity) > 0 && Number(l.rate) >= 0 && l.rate !== '');
  const canPost = customer && linesValid && !submitting && writesEnabled !== false;

  const post = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const path = docType === 'creditMemo' ? '/api/native-invoices/credit-memos' : '/api/native-invoices';
      const { data } = await axios.post(path, {
        sourceKey: sourceKey.current,
        customer: { nsId: customer.nsCustomerId },
        ...(soInfo?.nsSoId ? { fromNsSoId: soInfo.nsSoId } : {}),
        lines: lines.map((l) => ({
          itemIdOrSku: l.itemIdOrSku,
          quantity: Number(l.quantity),
          rate: Number(l.rate),
        })),
        shippingTotal: shipping || undefined,
        memo: memo.trim() || undefined,
        date,
        dueDate: dueDate || undefined,
      });
      setConfirmation(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      sourceKey.current = newRef(docType); // a failed post gets a fresh idempotency key
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const d = confirmation.invoice || confirmation.creditMemo || confirmation.doc;
    const number = d.invoice_number || d.credit_memo_number;
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{number} posted</h2>
          <div className="nia-confirm-grid">
            <span>Customer</span><strong>{d.customer_name}</strong>
            <span>Subtotal</span><strong>{money(d.subtotal)}</strong>
            <span>Shipping</span><strong>{money(d.shipping_total)}</strong>
            <span>Total</span><strong>{money(d.total)}</strong>
            {d.due_date && (<><span>Due</span><strong>{d.due_date}</strong></>)}
            <span>GL</span><strong>{d.gl_status}{d.gl_error ? ` (${d.gl_error})` : ''}</strong>
            <span>NetSuite</span><strong>{d.ns_status}{d.ns_transaction_id ? ` (${d.ns_transaction_id})` : ''}</strong>
          </div>
          <p className="nia-note">{confirmation.netsuiteNote}</p>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={() => window.location.reload()}>New document</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><FileText size={20} /> {docType === 'creditMemo' ? 'Credit Memo' : 'Invoice'} (Native)</h1>
        {writesEnabled === false && (
          <div className="nia-banner">Posting is disabled (NATIVE_INVOICES_ENABLED is off on the Run service). The form works; posting will 403.</div>
        )}
      </div>

      <div className="nia-card">
        <h3>1. Document type</h3>
        <div className="nia-locations">
          <button className={`nia-location ${docType === 'invoice' ? 'selected' : ''}`} onClick={() => setDocType('invoice')}>Invoice</button>
          <button className={`nia-location ${docType === 'creditMemo' ? 'selected' : ''}`} onClick={() => setDocType('creditMemo')}>Credit memo</button>
        </div>
        <p className="nia-hint" style={{ marginTop: 8 }}>
          {docType === 'creditMemo'
            ? 'A credit memo reduces what the customer owes (credits A/R, debits the income accounts).'
            : 'An invoice bills the customer (debits A/R 1110, credits the item income accounts). It does not move inventory — fulfillment does.'}
        </p>
      </div>

      <div className="nia-card">
        <h3>2. Customer</h3>
        {!customer ? (
          <>
            <input
              className="nia-input nia-input-search"
              placeholder="Search customers by name or email…"
              value={customerQuery}
              onChange={(e) => setCustomerQuery(e.target.value)}
            />
            {customerResults.length > 0 && (
              <div className="nia-results">
                {customerResults.map((r) => (
                  <button key={r.nsCustomerId} className="nia-result" onClick={() => { setCustomer(r); setCustomerQuery(''); setCustomerResults([]); }}>
                    {r.name}
                    <span className="nia-result-muted">{r.email || r.contact || ''} · {r.source}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="nia-chip">
            {customer.name}
            {customer.email ? <span className="nia-result-muted"> {customer.email}</span> : null}
            <button className="nia-icon-btn" onClick={() => setCustomer(null)}>×</button>
          </div>
        )}
      </div>

      <div className="nia-card">
        <h3>3. Lines</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 }}>
          <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Prefill from sales order (mirror)
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="nia-input" placeholder="SO number or NS id" value={soRef} onChange={(e) => setSoRef(e.target.value)} />
              <button className="nia-btn" onClick={loadFromSo} disabled={soLoading || !soRef.trim()}>
                {soLoading ? 'Loading…' : 'Load'}
              </button>
            </div>
          </label>
          {soInfo && <span className="nia-hint">Loaded {soInfo.orderNumber} — {soInfo.customerName}</span>}
        </div>

        <input
          className="nia-input nia-input-search"
          placeholder="Add a line: search items by SKU or name…"
          value={itemQuery}
          onChange={(e) => setItemQuery(e.target.value)}
        />
        {itemResults.length > 0 && (
          <div className="nia-results">
            {itemResults.map((r) => (
              <button key={r.id} className="nia-result" onClick={() => addItem(r)}>
                <code>{r.sku}</code> {r.name}
              </button>
            ))}
          </div>
        )}

        {lines.length > 0 && (
          <table className="nia-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Item</th>
                <th className="r">Qty</th>
                <th className="r">Rate</th>
                <th className="r">Amount</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.key}>
                  <td><code>{l.sku}</code> {l.name}</td>
                  <td className="r">
                    <input className="nia-input nia-input-num" inputMode="decimal" value={l.quantity}
                      onChange={(e) => setLine(l.key, 'quantity', e.target.value)} />
                  </td>
                  <td className="r">
                    <input className="nia-input nia-input-num" inputMode="decimal" value={l.rate}
                      onChange={(e) => setLine(l.key, 'rate', e.target.value)} />
                  </td>
                  <td className="r">{money((Number(l.quantity) || 0) * (Number(l.rate) || 0))}</td>
                  <td><button className="nia-icon-btn" onClick={() => removeLine(l.key)}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="nia-card">
        <h3>4. Details</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Date
            <input className="nia-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          {docType === 'invoice' && (
            <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Due date (blank = from customer terms)
              <input className="nia-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </label>
          )}
          <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            Shipping
            <input className="nia-input nia-input-num" inputMode="decimal" value={shippingTotal}
              onChange={(e) => setShippingTotal(e.target.value)} placeholder="0.00" />
          </label>
          <label className="nia-hint" style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 220 }}>
            Memo (optional)
            <input className="nia-input" value={memo} onChange={(e) => setMemo(e.target.value)} maxLength={500} />
          </label>
        </div>
        <div className="nia-footer" style={{ marginTop: 12 }}>
          <div className="nia-footer-totals">
            <span>Subtotal <strong>{money(subtotal)}</strong></span>
            <span>Shipping <strong>{money(shipping)}</strong></span>
            <span>Total <strong>{money(total)}</strong></span>
          </div>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-primary" onClick={post} disabled={!canPost}>
              {submitting ? 'Posting…' : docType === 'creditMemo' ? 'Post credit memo' : 'Post invoice'}
            </button>
          </div>
        </div>
        {error && <div className="nia-error" style={{ marginTop: 8 }}>{error}</div>}
        <p className="nia-hint" style={{ marginTop: 8 }}>
          Posting writes the native document and its journal entry immediately; the NetSuite copy is
          best-effort (retryable) and never emails the customer. Voiding later is refused while payments
          are applied to the document.
        </p>
      </div>
    </div>
  );
}
